import { extends as _extends, objectWithoutProperties as _objectWithoutProperties } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { Box, Image, ButtonGroup, Button, TextButton, IconCloseOutline, IconMenuOutline } from '@zillow/constellation';
import { yieldCallback } from '@zillow/yield-callback';
import React from 'react';
import { GROUP_WIDTH_MAP } from '../../mixins/components/GroupMixin.js';
import { getCmsInfoForPage } from '../../utils/clickstream/getCmsInfoForPage.js';
import { usePageProps } from '../PagePropsProvider/PagePropsProvider.js';
import { sendEvent } from './B2BMainNav.utils.js';
import MenuItem from './MenuItem.js';
import MenuList from './MenuList.js';

var _excluded = ["children"];
var TextButtonAnchor = function TextButtonAnchor(props) {
  return /*#__PURE__*/React.createElement(TextButton, _extends({}, props, {
    as: "a"
  }));
};

/**
 * Generates a main navigation component based on the provided props.
 *
 * @param {object} props - The props object containing primary and secondary CTAs, menu items, and other navigation information.
 * @return {JSX.Element} The JSX element representing the main navigation component.
 */
var B2BMainNav = function B2BMainNav(props) {
  var menu = props.menu,
    mobileMenu = props.mobileMenu,
    setMobileMenu = props.setMobileMenu,
    primaryCTALink = props.primaryCTALink,
    primaryCTALabel = props.primaryCTALabel,
    secondaryCTALink = props.secondaryCTALink,
    secondaryCTALabel = props.secondaryCTALabel,
    logo = props.logo,
    disabled = props.disabled,
    transparent = props.transparent,
    sticky = props.sticky;
  var MobileMenuIcon = mobileMenu ? IconCloseOutline : IconMenuOutline;

  /**
   * Parses the menu items recursively to create a menu structure.
   *
   * @param {Array} items - The list of menu items to parse.
   * @param {number} [root=0] - The root item to start parsing from.
   * @return {Array} An array representing the parsed menu structure.
   */
  function parseMenu(items, root) {
    if (root === void 0) {
      root = 0;
    }
    // Handle case when items is undefined or null
    if (!items || !Array.isArray(items)) {
      return [];
    }
    return items.filter(function (item) {
      return item.parent == root;
    }).map(function (menuItem) {
      var _menuItem$title, _menuItem$title2;
      try {
        new URL(menuItem.url);
      } catch (error) {
        console.warn("Failed to parse URL: " + menuItem.url);
      }
      return {
        id: menuItem.id,
        label: (menuItem === null || menuItem === void 0 || (_menuItem$title = menuItem.title) === null || _menuItem$title === void 0 ? void 0 : _menuItem$title.raw) || (menuItem === null || menuItem === void 0 || (_menuItem$title2 = menuItem.title) === null || _menuItem$title2 === void 0 ? void 0 : _menuItem$title2.rendered),
        current: false,
        // No router context, so always false
        url: menuItem.url,
        children: parseMenu(items.filter(function (child) {
          var _child$parent;
          return (child === null || child === void 0 || (_child$parent = child.parent) === null || _child$parent === void 0 ? void 0 : _child$parent.toString()) == menuItem.id;
        }), menuItem.id)
      };
    });
  }
  var menuItems = parseMenu(menu);
  var pageProps = usePageProps();
  var cmsInfo = getCmsInfoForPage(pageProps);

  /**
   * A function that handles the click event for Call to Actions (CTA).
   *
   * @param {string} triggerSource - The source of the click event.
   * @param {string} label - The label associated with the click event.
   * @param {string} url - The URL related to the click event.
   * @return {void} No return value.
   */
  var handleCTAClick = function handleCTAClick(triggerSource, label, url) {
    sendEvent(triggerSource, label, url, cmsInfo);
  };
  var LogoContainer = function LogoContainer(_ref) {
    var children = _ref.children,
      props = _objectWithoutProperties(_ref, _excluded);
    if (logo !== null && logo !== void 0 && logo.link && !disabled) {
      return /*#__PURE__*/React.createElement("a", props, children);
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, children);
  };
  var hasPrimaryCta = primaryCTALink && primaryCTALabel;
  var hasSecondaryCta = secondaryCTALink && secondaryCTALabel;
  var mobilePosition = 'relative';
  var mobileTop = '0';
  if (sticky) {
    mobilePosition = 'sticky';
  } else if (mobileMenu) {
    mobilePosition = 'absolute';
    mobileTop = '41px';
  }
  return (
    /*#__PURE__*/
    // Box to add border and background, allows to stretch to full width.
    React.createElement(Box, {
      sx: Object.assign({
        zIndex: '100',
        position: sticky ? 'fixed' : 'relative'
      }, sticky && {
        width: '100%'
      }, {
        top: 0,
        borderBottom: transparent ? '$borderWidths.xs solid $colors.white' : '$borderWidths.xs solid $colors.borderLight',
        background: transparent ? 'rgba(0, 0, 0, 0)' : '$colors.backgroundWhite',
        '@media $lg_lte': {
          position: mobilePosition,
          top: mobileTop,
          left: 0,
          right: 0,
          bottom: 0
        }
      })
    }, /*#__PURE__*/React.createElement(Box, {
      role: "navigation",
      "aria-label": "Main Navigation",
      sx: Object.assign({
        display: 'flex',
        gap: '$spacing.sm',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '$spacing.sm',
        margin: '0 auto',
        maxWidth: GROUP_WIDTH_MAP.xl
      }, mobileMenu && {
        background: '$colors.backgroundWhite'
      }, {
        '@media $lg_lte': {
          alignItems: 'start',
          flexDirection: 'column',
          gap: 0,
          padding: '$spacing.none'
        }
      })
    }, logo && /*#__PURE__*/React.createElement(Box, {
      sx: {
        '@media $lg_lte': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'unset',
          width: '100%',
          padding: '$spacing.sm'
        },
        '@media $md_lte': {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%'
        }
      }
    }, /*#__PURE__*/React.createElement(Box, {
      sx: {
        '@media $lg_lte': {
          flex: 1
        }
      }
    }, /*#__PURE__*/React.createElement(LogoContainer, {
      href: logo.link,
      style: {
        display: 'flex'
      }
    }, /*#__PURE__*/React.createElement(Box, {
      as: Image,
      src: logo.src,
      alt: logo.alt || 'Logo',
      height: logo.height,
      width: logo.width,
      sx: {
        display: 'block',
        maxHeight: '44px',
        filter: transparent ? 'saturate(0%) invert(100%) brightness(300%) contrast(100%)' : 'none'
      }
    }))), !disabled && /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: 'none',
        '@media $lg_lte': {
          display: 'block',
          marginRight: '$spacing.sm'
        },
        '@media $md_lte': {
          display: 'none'
        }
      }
    }, /*#__PURE__*/React.createElement(ButtonGroup, {
      "aria-label": "Calls to Action"
    }, secondaryCTALabel && secondaryCTALink && /*#__PURE__*/React.createElement(Box, {
      as: TextButtonAnchor,
      sx: {
        ':hover, :focus': {
          textDecoration: transparent ? 'underline !important' : 'none !important',
          color: transparent ? '$colors.textWhite' : '$colors.blue500'
        },
        ':not(:hover)': {
          textDecoration: 'none !important',
          color: transparent ? '$colors.textWhite' : '$colors.brand'
        }
      },
      href: secondaryCTALink,
      onClick: yieldCallback(function () {
        handleCTAClick('cms_button', secondaryCTALabel, secondaryCTALink);
      })
    }, secondaryCTALabel), primaryCTALabel && primaryCTALink && /*#__PURE__*/React.createElement(Button, {
      as: "a",
      href: primaryCTALink,
      buttonType: transparent ? 'secondary' : 'primary',
      onClick: yieldCallback(function () {
        handleCTAClick('cms_button', primaryCTALabel, primaryCTALink);
      })
    }, primaryCTALabel))), !disabled && /*#__PURE__*/React.createElement(Box, {
      sx: {
        '@media $xl': {
          display: 'none'
        }
      },
      role: "button",
      "aria-expanded": mobileMenu,
      "aria-label": "Mobile Menu",
      onClick: function onClick() {
        return setMobileMenu(function (prev) {
          return !prev;
        });
      }
    }, /*#__PURE__*/React.createElement(MobileMenuIcon, {
      size: "sm",
      fontColor: transparent ? 'white' : 'brand'
    }))), menu && !disabled && /*#__PURE__*/React.createElement(MenuList, {
      sx: {
        display: 'flex',
        flex: 'auto',
        height: '100%',
        alignItems: 'center',
        '@media $lg_lte': {
          display: mobileMenu ? 'flex' : 'none',
          flexDirection: 'column',
          alignItems: 'start',
          height: 'auto',
          width: '100%',
          borderTop: '$borderWidths.xs solid $colors.borderLight'
        }
      }
    }, menuItems.map(function (item) {
      return /*#__PURE__*/React.createElement(MenuItem, {
        key: item.id,
        item: item,
        transparent: transparent,
        sx: {
          display: 'flex',
          alignItems: 'end',
          whiteSpace: 'nowrap'
        }
      });
    })), (hasPrimaryCta || hasSecondaryCta) && !disabled && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Box, {
      sx: {
        '@media $lg_lte': {
          display: 'none'
        }
      }
    }, /*#__PURE__*/React.createElement(ButtonGroup, {
      "aria-label": "Call to Action buttons"
    }, hasSecondaryCta && /*#__PURE__*/React.createElement(Box, {
      as: TextButtonAnchor,
      sx: {
        ':hover, :focus': {
          textDecoration: transparent ? 'underline !important' : 'none !important'
        },
        ':not(:hover)': {
          textDecoration: 'none !important',
          color: transparent ? '$colors.textWhite' : '$colors.brand'
        }
      },
      inverse: transparent,
      href: secondaryCTALink,
      onClick: yieldCallback(function () {
        handleCTAClick('cms_button', secondaryCTALabel, secondaryCTALink);
      })
    }, secondaryCTALabel), hasPrimaryCta && /*#__PURE__*/React.createElement(Button, {
      as: "a",
      href: primaryCTALink,
      buttonType: transparent ? 'secondary' : 'primary',
      onClick: yieldCallback(function () {
        handleCTAClick('cms_button', primaryCTALabel, primaryCTALink);
      })
    }, primaryCTALabel))), /*#__PURE__*/React.createElement(Box, {
      sx: {
        display: 'none',
        '@media $md_lte': {
          display: mobileMenu ? 'block' : 'none',
          padding: '$spacing.sm',
          marginTop: '$spacing.sm',
          width: '100%'
        }
      }
    }, /*#__PURE__*/React.createElement(ButtonGroup, {
      fluid: true,
      "aria-label": "Call to Action buttons",
      direction: "column"
    }, hasPrimaryCta && /*#__PURE__*/React.createElement(Button, {
      as: "a",
      href: primaryCTALink,
      buttonType: "primary",
      onClick: yieldCallback(function () {
        handleCTAClick('cms_button', primaryCTALabel, primaryCTALink);
      })
    }, primaryCTALabel), hasSecondaryCta && /*#__PURE__*/React.createElement(Button, {
      as: "a",
      href: secondaryCTALink,
      buttonType: "secondary",
      onClick: yieldCallback(function () {
        handleCTAClick('cms_button', secondaryCTALabel, secondaryCTALink);
      })
    }, secondaryCTALabel))))))
  );
};
var B2BMainNav$1 = B2BMainNav;

export { B2BMainNav$1 as default };
